/** Minimum accepted secret length for compact-token keys, measured in characters. */
export const COMPACT_TOKEN_KEY_MIN_LENGTH = 32;

export type CompactTokenPayload = { exp: number };

export type CompactTokenVerifyResult<T extends CompactTokenPayload> =
  | { ok: true; payload: T }
  | { ok: false; reason: "malformed" | "signature" | "expired" };

const encoder = new TextEncoder();
const decoder = new TextDecoder("utf-8", { fatal: true });
const BASE64URL_PATTERN = /^[A-Za-z0-9_-]+$/;

function assertKey(key: string): void {
  if (key.length < COMPACT_TOKEN_KEY_MIN_LENGTH) {
    throw new Error(
      `compact token key must be at least ${COMPACT_TOKEN_KEY_MIN_LENGTH} characters, got ${key.length}`,
    );
  }
}

function assertExpiry(payload: CompactTokenPayload): void {
  if (!Number.isSafeInteger(payload.exp)) {
    throw new Error("compact token payload exp must be an integer Unix timestamp in seconds");
  }
}

function encodeBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function decodeBase64Url(value: string): Uint8Array {
  if (!BASE64URL_PATTERN.test(value) || value.length % 4 === 1) {
    throw new Error("invalid base64url");
  }

  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  if (encodeBase64Url(bytes) !== value) {
    throw new Error("non-canonical base64url");
  }
  return bytes;
}

async function importKey(key: string): Promise<CryptoKey> {
  assertKey(key);
  return crypto.subtle.importKey(
    "raw",
    encoder.encode(key),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign", "verify"],
  );
}

/** Creates a compact token: base64url(JSON payload) + "." + base64url(HMAC-SHA256). */
export async function signCompactToken<T extends CompactTokenPayload>(
  payload: T,
  key: string,
): Promise<string> {
  assertKey(key);
  assertExpiry(payload);

  const serialized = JSON.stringify(payload);
  if (serialized === undefined) {
    throw new Error("compact token payload must be JSON serializable");
  }

  const payloadPart = encodeBase64Url(encoder.encode(serialized));
  const cryptoKey = await importKey(key);
  const signature = await crypto.subtle.sign("HMAC", cryptoKey, encoder.encode(payloadPart));
  return `${payloadPart}.${encodeBase64Url(new Uint8Array(signature))}`;
}

/** Verifies a compact token and validates its decoded payload through the supplied parser. */
export async function verifyCompactToken<T extends CompactTokenPayload>(
  token: string,
  key: string,
  options: { parse: (value: unknown) => T; nowSeconds?: number },
): Promise<CompactTokenVerifyResult<T>> {
  assertKey(key);

  const parts = token.split(".");
  const payloadPart = parts[0];
  const signaturePart = parts[1];
  if (
    parts.length !== 2 ||
    payloadPart === undefined ||
    signaturePart === undefined ||
    !BASE64URL_PATTERN.test(payloadPart) ||
    !BASE64URL_PATTERN.test(signaturePart)
  ) {
    return { ok: false, reason: "malformed" };
  }

  let payloadBytes: Uint8Array;
  let signatureBytes: Uint8Array;
  let rawPayload: unknown;
  try {
    payloadBytes = decodeBase64Url(payloadPart);
    signatureBytes = decodeBase64Url(signaturePart);
    rawPayload = JSON.parse(decoder.decode(payloadBytes));
  } catch {
    return { ok: false, reason: "malformed" };
  }

  let payload: T;
  try {
    payload = options.parse(rawPayload);
    assertExpiry(payload);
  } catch {
    return { ok: false, reason: "malformed" };
  }

  const cryptoKey = await importKey(key);
  // Copy to an ArrayBuffer-backed view for the Web Crypto BufferSource type;
  // decoded input may otherwise retain a SharedArrayBuffer-capable type.
  const signatureBuffer = new ArrayBuffer(signatureBytes.byteLength);
  new Uint8Array(signatureBuffer).set(signatureBytes);
  const signatureValid = await crypto.subtle.verify(
    "HMAC",
    cryptoKey,
    signatureBuffer,
    encoder.encode(payloadPart),
  );
  if (!signatureValid) {
    return { ok: false, reason: "signature" };
  }

  const nowSeconds = options.nowSeconds ?? Math.floor(Date.now() / 1000);
  if (payload.exp < nowSeconds) {
    return { ok: false, reason: "expired" };
  }

  return { ok: true, payload };
}
